/**
 * Firestore implementation of RequisitionRepository.
 *
 * Path: purchaseRequisitions/{id}
 *
 * Purchase requisitions — internal "pay this vendor" documents raised from
 * the adhoc line items on a quote. See src/types/purchaseRequisition.ts.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { PurchaseRequisition, EntityId } from '../../../types';
import type { RequisitionRepository } from '../types';

const COLLECTION = 'purchaseRequisitions';

export const firestoreRequisitionRepository: RequisitionRepository = {
  async findById(id: EntityId): Promise<PurchaseRequisition | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    return snap.exists() ? (snap.data() as PurchaseRequisition) : null;
  },

  async save(entity: PurchaseRequisition): Promise<void> {
    await setDoc(doc(db, COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async list(): Promise<PurchaseRequisition[]> {
    const snap = await getDocs(collection(db, COLLECTION));
    return snap.docs.map((d) => d.data() as PurchaseRequisition);
  },

  async findByOrder(orderId: EntityId): Promise<PurchaseRequisition[]> {
    // Single-field query on `orderId`. The dual-field (`projectId` OR `orderId`)
    // migration read was retired 2026-09-04 (backlog #80): prod carried zero
    // `projectId`-only docs and the extra query ran on every order-detail visit.
    const snap = await getDocs(query(collection(db, COLLECTION), where('orderId', '==', orderId)));
    return snap.docs.map((d) => d.data() as PurchaseRequisition);
  },
};
